import { useEffect, useMemo, useRef, useState } from "react";
import { LiFiWidget, useWidgetEvents, WidgetEvent } from "@lifi/widget";
import {
    LuArrowRightLeft,
    LuCircleAlert,
    LuCircleHelp,
    LuReceipt,
} from "react-icons/lu";
import { useTheme } from "../contexts/ThemeContext";
import { useConnectWallet } from "../hooks/useConnectWallet";
import UsdxSmartSell, { USDXSMART } from "./UsdxSmartSell";
// import ThirdwebWagmiProvider from "./ThirdwebWagmiProvider";

/* Cross-chain transfers, run by the Li.Fi widget.
 *
 * The integrator string below is the one registered with Li.Fi, and the API key
 * it is paired with comes from REACT_APP_LIFI_API_KEY.
 *
 * Fee collection is ON at 0.5% (`feeConfig` below). Li.Fi takes it from the
 * sending token on the source chain and folds it into the quote rather than
 * charging separately, so the recipient receives that much less.
 *
 * `showFeePercentage: false` keeps the widget from printing the percentage in
 * its own fee breakdown. The "What it costs" panel on this page states it
 * instead — if either changes, change the other with it.
 */

const INTEGRATOR = "smart-usdx";
const BRAND = "SwapX";

/* The widget renders through MUI and cannot read our CSS variables, so the
   --u-* tokens are mirrored here as literals. */
const TOKENS = {
    light: {
        accent: "#066FD2",
        accentSoft: "rgba(6,111,210,0.1)",
        accentSoftHover: "rgba(6,111,210,0.18)",
        onAccent: "#FFFFFF",
        ink: "#0B2545",
        inkMuted: "rgba(11,37,69,0.65)",
        surface: "#FFFFFF",
        surfaceSunken: "#EEF4FA",
        canvas: "#F5F9FD",
        line: "rgba(6,111,210,0.15)",
    },
    dark: {
        accent: "#5CACF2",
        accentSoft: "rgba(92,172,242,0.12)",
        accentSoftHover: "rgba(92,172,242,0.22)",
        onAccent: "#0A1626",
        ink: "#E9F1F9",
        inkMuted: "rgba(233,241,249,0.62)",
        surface: "#12233A",
        surfaceSunken: "#1C3149",
        canvas: "#081220",
        line: "rgba(92,172,242,0.16)",
    },
};

/* Both schemes are declared, not just the active one. The widget's theme is
   built on MUI CSS variables keyed by colour scheme, and a flat `palette` left
   the dark scheme's variables undefined for some of its own components — the
   connected-wallet button then inherited MUI's stock near-black text and the
   address became unreadable on the dark surface. */
const schemeFor = (t) => ({
    palette: {
        primary: { main: t.accent, contrastText: t.onAccent },
        secondary: { main: t.accent },
        text: { primary: t.ink, secondary: t.inkMuted },
        background: { paper: t.surface, default: t.canvas },
        grey: { 800: t.surfaceSunken },
    },
});

const COSTS = [
    // {
    //     k: "Gas on the source chain",
    //     v: "Paid by you",
    //     d: "The approval and the transfer are ordinary transactions, priced in that chain's native token.",
    // },
    {
        k: "Route fee",
        v: "Inside the quote",
        d: "Whatever the bridge and any DEX hop charge is already reflected in the amount you are quoted.",
    },
    // {
    //     k: "Our cut",
    //     v: "0.10%",
    //     d: "Taken on top of the route and already folded into the amount you are quoted, so the figure you see is what arrives.",
    // },
    {
        k: "Gas on arrival",
        v: "Sometimes included",
        d: "Some routes deliver a little native token with the transfer. Where they cannot, you will need some before you can move the funds again.",
    },
    {
        k: "Time to settle",
        v: "Usually minutes",
        d: "Most of it is waiting for the source chain to finalise, so it varies by chain and by route.",
    },
];

const TROUBLE = [
    {
        q: "The route changed when I went to confirm",
        a: "Quotes are priced when they are fetched and liquidity moves underneath them. Refetch, read the new output amount, and confirm from that.",
    },
    {
        q: "It has been pending longer than I expected",
        a: "Bridges wait for the source chain to finalise before releasing on the other side, and that wait differs by chain. The widget keeps tracking the transfer — leave it open rather than resending.",
    },
    {
        q: "The tokens have not turned up",
        a: "Open the transfer from the widget's history and follow the destination transaction on that chain's explorer. Some routes settle in two steps, so the second one may still be in flight.",
    },
    {
        q: "My wallet is on the wrong network",
        a: "The widget asks your wallet to switch before it builds the transaction. Approve the switch there, or change networks in the wallet and come back.",
    },
];

const NEVER = [
    "Hold your tokens at any point",
    "Ask for a seed phrase or private key",
    "Move funds without a signature from you",
];

/* Decorative coins beside the widget: a large one low on the left, a
   medium and a small one high on the right, each tilted a little. */
const COINS = [
    { className: "w-44 -left-10 bottom-16 -rotate-12 blur-[1px]", opacity: 0.12 },
    { className: "w-16 left-24 top-24 rotate-6", opacity: 0.08 },
    { className: "w-32 -right-6 top-16 rotate-12", opacity: 0.1 },
    { className: "w-20 right-20 bottom-40 -rotate-6 blur-[1px]", opacity: 0.07 },
];

/* How a USDXSMART sale is routed when it is picked as the source token.
 *
 *   true  — hand over to UsdxSmartSell, which sells straight through the
 *           Uniswap V2 router. Li.Fi's fee does not apply there, so
 *           UsdxSmartSell swaps its own 1% to the treasury first.
 *   false — leave it in the Li.Fi widget like any other token. Li.Fi's fee
 *           applies, but today every such sale reverts ("Must have $1000
 *           staked to sell"): the token checks the stake of the address moving
 *           tokens into its pool, and on Li.Fi's route that is an aggregator
 *           contract, not the user's wallet.
 */
const USDXSMART_VIA_UNISWAP = true;

const isUsdxSmart = (chainId, tokenAddress) =>
    USDXSMART_VIA_UNISWAP &&
    chainId === USDXSMART.chainId &&
    tokenAddress?.toLowerCase() === USDXSMART.address.toLowerCase();

const SECTIONS = [
    { id: "transfer", label: "Transfer", Icon: LuArrowRightLeft },
    { id: "costs", label: "What it costs", Icon: LuReceipt },
    { id: "help", label: "If it misbehaves", Icon: LuCircleHelp },
];

const LiFiBridge = () => {
    const { theme } = useTheme();
    const isDark = theme !== "light";
    const t = isDark ? TOKENS.dark : TOKENS.light;
    const [section, setSection] = useState("transfer");
    const { openConnect } = useConnectWallet({ chain: null });
    const widgetEvents = useWidgetEvents();
    const [sellingUsdxSmart, setSellingUsdxSmart] = useState(false);
    const formRef = useRef(null);

    /* Clears the widget's source token and brings the widget back. */
    const pickAnotherToken = () => {
        formRef.current?.setFieldValue("fromToken", undefined);
        setSellingUsdxSmart(false);
    };

    useEffect(() => {
        const onSourceToken = ({ chainId, tokenAddress }) =>
            setSellingUsdxSmart(isUsdxSmart(chainId, tokenAddress));
        widgetEvents.on(WidgetEvent.SourceChainTokenSelected, onSourceToken);
        return () =>
            widgetEvents.off(WidgetEvent.SourceChainTokenSelected, onSourceToken);
    }, [widgetEvents]);

    const widgetConfig = useMemo(
        () => ({
            integrator: INTEGRATOR,
            apiKey: process.env.REACT_APP_LIFI_API_KEY,
            /* `name` and `logoURI` label our cut as SwapX in the fee breakdown
               and in the route's step list, instead of "Integrator fee". */
            feeConfig: {
                fee: 0.005,
                showFeePercentage: false,
                name: BRAND,
                logoURI: `${window.location.origin}/usdx-icon.png`,
            },

            // Disable EIP-7702 bundled transactions — prevents MetaMask bundle errors
            useRelayerRoutes: false,

            /* Quote only the best route: no "Show all" list, and no side panel
               of alternatives in the wide layout. */
            useRecommendedRoute: true,

            /* No OKX routes. On Base they are 2–3x heavier than the other
               DEXs (~1.75M gas for USDC→ETH against ~0.5M via Nordstern),
               and mobile in-app wallets fail them before sending — the user
               sees only "An unknown RPC error occurred". The same wallets
               complete the lighter routes. */
            exchanges: { deny: ["okx"] },

            appearance: isDark ? "dark" : "light",
            variant: "wide",

            /* No Li.Fi attribution, per-route "via" text, or Li.Fi's "Contact support"
               button on transaction details. */
            hiddenUI: ["poweredBy", "routeTokenDescription", "contactSupport"],

            /* Li.Fi's chain list includes Base Sepolia (84532); keep that
               testnet out of the "From" and "To" pickers. */
            chains: { deny: [84532] },

            /* The widget uses the wallet connected through our header (see
               ThirdwebWagmiProvider), so its own wallet menu is gone and its
               "Connect wallet" button opens our modal instead of Li.Fi's. */
            walletConfig: { onConnect: openConnect },

            /* SwapX in place of the widget's own wording: the title over the
               form reads "Exchange", and the step row "<bridge> via LI.FI". */
            /* Pinned to English so the overrides below always apply: they only
               cover English, and a language the widget saved earlier in this
               browser would otherwise win. This also hides its language picker. */
            languages: { default: "en", allow: ["en"] },
            languageResources: {
                en: {
                    header: { exchange: BRAND },
                    main: { stepDetails: `{{tool}} via ${BRAND}` },
                },
            },

            theme: {
                colorSchemes: {
                    light: schemeFor(TOKENS.light),
                    dark: schemeFor(TOKENS.dark),
                },
                shape: { borderRadius: 16, borderRadiusSecondary: 12 },
                typography: { fontFamily: "inherit" },
                container: {
                    boxShadow: `0 0 0 1px ${t.line}`,
                    borderRadius: "16px",
                },
            },
            components: {
                MuiIconButton: {
                    styleOverrides: {
                        root: {
                            backgroundColor: t.accentSoft,
                            color: t.accent,
                            borderRadius: "12px",
                            "&:hover": { backgroundColor: t.accentSoftHover },
                        },
                    },
                },
            },
        }),
        [isDark, t, openConnect],
    );

    return (
        /* One console, not a column of cards: a toolbar across the top, a rail of
           sections down the side, and a single pane that changes with them. */
        <div className="w-full max-w-[1040px] text-left">
            <section
                className="card-3d overflow-hidden"
                style={{
                    /* The widget paints its own surfaces; the glass fill behind
                       them muddies the result, so this card opts out of it. */
                    background: t.surface,
                    backgroundImage: "none",
                }}
            >
                {/* Toolbar */}
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-usdx-line">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <span className="w-8 h-8 rounded-xl flex items-center justify-center bg-usdx-accent/10 text-usdx-accent shrink-0">
                            <LuArrowRightLeft className="size-[16px]" aria-hidden="true" />
                        </span>
                        <div className="min-w-0">
                            <h1 className="text-[15px] font-bold text-usdx-ink leading-tight">
                                Cross-chain transfer
                            </h1>
                            <p className="text-[11px] text-usdx-ink-subtle leading-tight mt-0.5">
                                One run can change both the network and the token
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <span className="token-pill-3d inline-flex items-center gap-2 px-3 py-1.5">
                            <span
                                className="w-1.5 h-1.5 rounded-full bg-usdx-success"
                                aria-hidden="true"
                            />
                            <span className="text-[11px] font-bold text-usdx-ink-muted">
                                Live
                            </span>
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[196px_minmax(0,1fr)]">

                    {/* Section rail — upright beside the pane, lying down above it
                        on a phone. */}
                    <nav
                        aria-label="Bridge sections"
                        className="flex md:flex-col gap-1 p-2 sm:p-3 overflow-x-auto no-scrollbar border-b md:border-b-0 md:border-r border-usdx-line"
                    >
                        {SECTIONS.map(({ id, label, Icon }) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setSection(id)}
                                aria-current={section === id ? "true" : undefined}
                                className={`console-nav-item ${
                                    section === id ? "console-nav-item-active" : ""
                                }`}
                            >
                                <Icon className="size-[15px] shrink-0" aria-hidden="true" />
                                {label}
                            </button>
                        ))}
                    </nav>

                    <div className="min-w-0 p-3 sm:p-4">

                        {/* The widget stays mounted whichever section is showing —
                            unmounting it would throw away a half-filled form. */}
                        <div hidden={section !== "transfer"} className="relative">
                            {/* Faint USDX coins in the empty space either side
                                of the widget. They sit in their own clipped layer
                                so the widget's drawers are never cut off, and
                                only appear once there is room beside it. */}
                            <div
                                className="hidden lg:block absolute inset-0 overflow-hidden pointer-events-none select-none"
                                aria-hidden="true"
                            >
                                {COINS.map(({ className, opacity }) => (
                                    <img
                                        key={className}
                                        src="/usdx-logo.png"
                                        alt=""
                                        draggable="false"
                                        className={`absolute ${className}`}
                                        style={{ opacity: isDark ? opacity * 0.7 : opacity }}
                                    />
                                ))}
                            </div>
                            {sellingUsdxSmart && (
                                <div className="relative">
                                    <UsdxSmartSell
                                        onPickAnother={pickAnotherToken}
                                        t={t}
                                        brand={BRAND}
                                    />
                                </div>
                            )}
                            <div
                                className="relative lifi-widget-host"
                                hidden={sellingUsdxSmart}
                            >
                                {/* <ThirdwebWagmiProvider> */}
                                    <LiFiWidget
                                        key={isDark ? "dark" : "light"}
                                        config={widgetConfig}
                                        integrator={INTEGRATOR}
                                        formRef={formRef}
                                    />
                                {/* </ThirdwebWagmiProvider> */}
                            </div>
                        </div>

                        {section === "costs" && (
                            <div className="max-w-[560px]">
                                <dl className="flex flex-col">
                                    {COSTS.map(({ k, v, d }) => (
                                        <div
                                            key={k}
                                            className="py-3 border-b border-usdx-line-soft last:border-b-0"
                                        >
                                            <div className="flex items-baseline justify-between gap-3">
                                                <dt className="text-[13px] font-bold text-usdx-ink">
                                                    {k}
                                                </dt>
                                                <dd className="text-[12px] font-semibold text-usdx-accent shrink-0">
                                                    {v}
                                                </dd>
                                            </div>
                                            <p className="text-[11px] text-usdx-ink-subtle leading-relaxed mt-1">
                                                {d}
                                            </p>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        )}

                        {section === "help" && (
                            <div className="max-w-[560px] flex flex-col gap-2">
                                {TROUBLE.map(({ q, a }) => (
                                    /* <details> keeps these keyboard-operable and
                                       findable by in-page search with no state of
                                       our own. */
                                    <details key={q} className="pool-card-3d px-3.5 py-2.5 group">
                                        <summary className="text-[12px] font-bold text-usdx-ink cursor-pointer list-none flex items-center justify-between gap-3">
                                            {q}
                                            <span
                                                className="text-usdx-ink-subtle text-[17px] leading-none shrink-0 transition-transform duration-200 group-open:rotate-45"
                                                aria-hidden="true"
                                            >
                                                +
                                            </span>
                                        </summary>
                                        <p className="text-[11px] text-usdx-ink-subtle leading-relaxed mt-2">
                                            {a}
                                        </p>
                                    </details>
                                ))}

                                <div className="inner-3d mt-1 px-3.5 py-3 flex items-start gap-2.5">
                                    <LuCircleAlert
                                        className="size-[15px] text-usdx-accent shrink-0 mt-0.5"
                                        aria-hidden="true"
                                    />
                                    <div>
                                        <p className="text-[12px] font-bold text-usdx-ink">
                                            This page will never
                                        </p>
                                        <ul className="mt-1 flex flex-col gap-0.5">
                                            {NEVER.map((n) => (
                                                <li
                                                    key={n}
                                                    className="text-[11px] text-usdx-ink-subtle leading-relaxed"
                                                >
                                                    {n}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default LiFiBridge;
